import React, { useState, useEffect } from "react";
import { get } from "../../../services/api";
import PersonalInfoForm from "./PersonalInfoForm";
import GuardianInfoForm from "./GuardianInfoForm";
import DocumentsForm from "./DocumentsForm";
import MedicalForm from "./MedicalForm";
import Siblings from "./Siblings";
import AIRecommendation from "./AIRecommendation";
import StudentElementsTab from "./StudentElementsTab";
import StudentLedgerTab from "./StudentLedgerTab";
import FaceRegistrationTab from "./FaceRegistrationTab";

const StudentProfile = ({ student, onClose }) => {
  const [activeTab, setActiveTab] = useState("pupil-information");
  const [profilePicSrc, setProfilePicSrc] = useState("");
  const [cacheBust, setCacheBust] = useState(0);
  const [primaryContactMobile, setPrimaryContactMobile] = useState("");
  const [parentCnic, setParentCnic] = useState("");

  const token = localStorage.getItem("token");

  const fetchStudentDocuments = async () => {
    if (!token || !student.studentFirstRegistrationId) {
      console.error("Unauthorized or no student ID.");
      return;
    }

    try {
      const response = await get(
        `InspStudentFirstRegistrtion/GetStudentPersonalProfileInfo/${student.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        if (response.studentDocuments) {
          const profileDoc = response.studentDocuments.find(
            (doc) => doc.documentType === "pupilPhoto"
          );
          if (profileDoc) {
            setProfilePicSrc(
              `${process.env.REACT_APP_LIVE_DOMAIN_URL.replace(
                /\/+$/,
                ""
              )}/ExternalImages${profileDoc.filePath}`
            );
            setCacheBust(Date.now());
          } else {
            setProfilePicSrc("");
          }
        }

        if (response.studentFamilyInfoDto) {
          setPrimaryContactMobile(
            response.studentFamilyInfoDto.primaryContactMobile || ""
          );
          setParentCnic(response.studentFamilyInfoDto.cnic || "");
        }
      }
    } catch (error) {
      console.error("Error fetching student documents:", error);
    }
  };

  useEffect(() => {
    if (student?.id && token) {
      fetchStudentDocuments();
    }
  }, [student?.id, token]);

  const tabComponents = {
    "pupil-information": PersonalInfoForm,
    "guardian-info": GuardianInfoForm,
    "admission-documents": DocumentsForm,
    "medical-form": MedicalForm,
    "sibling-form": Siblings,
    "ledger-record": StudentLedgerTab,
    "elements": StudentElementsTab,
    "ai-recommendation": AIRecommendation,
    "face-registration": FaceRegistrationTab,
  };

  const ActiveComponent = tabComponents[activeTab] || PersonalInfoForm;

  const refreshProfilePicture = async () => {
    await fetchStudentDocuments();
    setCacheBust(Date.now());
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block", zIndex: 1050, overflow: "hidden" }}
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content profile-modal-content">
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              style={{
                position: "absolute",
                top: "15px",
                right: "20px",
                zIndex: "10",
              }}
            ></button>

            <div className="profile-layout-container">
              {/* LEFT SIDEBAR */}
              <div className="profile-sidebar">
                <div className="profile-sidebar-header">
                  <div className="profile-picture">
                    {profilePicSrc ? (
                      <img
                        key={profilePicSrc + cacheBust}
                        src={
                          profilePicSrc.startsWith("data:")
                            ? profilePicSrc
                            : `${profilePicSrc}?t=${cacheBust}`
                        }
                        alt="Student"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://via.placeholder.com/120?text=No+Image";
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#e9ecef",
                          borderRadius: "50%",
                        }}
                      >
                        <i
                          className="fa fa-user"
                          style={{ fontSize: 50, color: "#aaa" }}
                        ></i>
                      </div>
                    )}
                  </div>
                  <h5 className="mb-1 fw-bold mt-3">
                    {student.firstName} {student.lastName}
                  </h5>
                  <p className="mb-1 fs-7 text-muted">
                    {student.className} - {student.sectionName || ""}
                  </p>
                  <div className="badge bg-light text-dark px-3 py-2 rounded-pill fs-9 mt-2 shadow-sm">
                    Student CODE: {student.code || "N/A"}
                  </div>
                </div>

                <div className="profile-sidebar-details">
                  <h6 className="details-header">Contact Information</h6>
                  <ul className="list-unstyled mb-0">
                    {student.email ? (
                      <li className="d-flex align-items-center mb-2">
                        <i className="bi bi-envelope me-2"></i>
                        <span>{student.email}</span>
                      </li>
                    ) : (
                      <li className="d-flex align-items-center mb-2 text-muted fst-italic">
                        <i className="bi bi-envelope-slash me-2"></i>
                        <span>No email provided</span>
                      </li>
                    )}

                    {primaryContactMobile || student.phone ? (
                      <li className="d-flex align-items-center">
                        <i className="bi bi-phone me-2"></i>
                        <span>{primaryContactMobile || student.phone}</span>
                      </li>
                    ) : (
                      <li className="d-flex align-items-center text-muted fst-italic">
                        <i className="bi bi-phone-slash me-2"></i>
                        <span>No phone provided</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              {/* RIGHT CONTENT AREA */}
              <div className="profile-content-wrapper">
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "pupil-information" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("pupil-information")}
                    >
                      Student Information
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "guardian-info" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("guardian-info")}
                    >
                      Parent Information
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "admission-documents" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("admission-documents")}
                    >
                      Admission Documents
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "medical-form" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("medical-form")}
                    >
                      Medical Form
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "sibling-form" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("sibling-form")}
                    >
                      Siblings
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "ledger-record" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("ledger-record")}
                    >
                      Student Ledger Report
                    </button>
                  </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "elements" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("elements")}
                      >
                        Elements
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "ai-recommendation" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("ai-recommendation")}
                      >
                        AI Recommendation
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "face-registration" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("face-registration")}
                      >
                        Register Attendance
                      </button>
                    </li>
                </ul>

                <div className="profile-content-area">
                  <div className="profile-scrollable-area">
                    <ActiveComponent
                      selectedStudent={student}
                      setProfilePicSrc={setProfilePicSrc}
                      refreshProfilePicture={refreshProfilePicture}
                      parentCnic={parentCnic}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === UPDATED STYLES === */}
      <style jsx>{`
        /* Brand color variables from original */
        :root {
          --brand-color: #b22222;
          --brand-hover: #8b1a1a;
          --tab-bg: #ffffff;
          --tab-border: #dee2e6;
          --tab-text: #111827;
        }

        /* Modal backdrop */
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1040;
        }

        /* Main modal content container */
        .profile-modal-content {
          border-radius: 16px; /* Softer, modern corners */
          overflow: hidden; /* Crucial for 2-column layout */
          border: none;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          height: 90vh; /* Make the modal tall */
          max-height: 900px; /* Max height */
        }

        /* 2-Column Flex Layout */
        .profile-layout-container {
          display: flex;
          width: 100%;
          height: 100%; /* Fill the modal content */
        }

        /* === Left Sidebar Styling === */
        .profile-sidebar {
          width: 300px;
          flex-shrink: 0;
          background: #f8f9fa; /* Light, clean background */
          border-right: 1px solid #dee2e6;
          padding: 2.5rem 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          overflow-y: auto;
        }

        .profile-sidebar-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }

        .profile-picture {
          width: 120px; /* Larger picture */
          height: 120px;
          border-radius: 50%;
          background-size: cover;
          background-position: center;
          border: 4px solid #ffffff;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .profile-sidebar-details {
          margin-top: 2rem;
          width: 100%;
          text-align: left;
        }

        .details-header {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6c757d; /* Muted text */
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 0.5rem;
          margin-bottom: 1rem;
        }

        .profile-sidebar-details ul li {
          font-size: 0.9rem;
          color: #212529;
          word-break: break-word; /* Wrap long emails */
        }

        /* Style for icons (assumes Bootstrap Icons) */
        .profile-sidebar-details i {
          color: var(--brand-color);
          width: 24px;
          flex-shrink: 0;
          text-align: center;
          font-size: 1.1rem;
        }

        /* === Right Content Area Styling === */
        .profile-content-wrapper {
          flex-grow: 1;
          padding: 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          overflow: hidden; /* IMPORTANT: for nested scroll */
        }

        /* Tab styling (adapted from original for new look) */
        .nav-pills {
          flex-shrink: 0; /* Don't let tabs shrink */
        }

        .nav-pills .nav-link {
          color: #495057;
          font-weight: 500;
          border-radius: 6px;
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s ease-in-out;
          border: 1px solid transparent;
        }

        .nav-pills .nav-link:hover {
          background: #e9ecef;
          color: #000;
        }

        .nav-pills .nav-link.active {
          background: var(--brand-color);
          color: #fff;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(178, 34, 34, 0.3);
          border-color: var(--brand-color);
        }

        /* Content Area (for scrolling) */
        .profile-content-area {
          flex-grow: 1; /* Takes remaining space */
          min-height: 0; /* CRITICAL for flex-grow to work with overflow */
        }

        .profile-scrollable-area {
          height: 100%; /* Fill the parent */
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 10px; /* Space for scrollbar */
          padding-left: 5px;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #aaa;
        }

        /* Responsive Handling */
        @media (max-width: 992px) {
          .profile-modal-content {
            height: 90vh;
            overflow-y: auto; /* Make the whole modal scroll */
          }
          .profile-layout-container {
            flex-direction: column; /* Stack sidebar and content */
            height: auto; /* Let content flow */
          }
          .profile-sidebar {
            width: 100%;
            height: auto;
            border-right: none;
            border-bottom: 1px solid #dee2e6;
            padding: 1.5rem;
          }
          .profile-content-wrapper {
            padding: 1rem;
          }
          .profile-content-area {
            min-height: auto; /* Unset flex behavior */
          }
          .profile-scrollable-area {
            height: auto; /* Unset height */
            overflow-y: visible; /* Disable nested scroll */
          }
          /* Original responsive tabs */
          .nav-pills {
            overflow-x: auto;
            overflow-y: hidden;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </>
  );
};

export default StudentProfile;
